/**
 * Navigation configuration for the admin dashboard sidebar.
 * Centralized navigation structure used by the shadcn/ui Sidebar component.
 *
 * @see /src/components/layout/Sidebar.tsx - Main sidebar component
 * @see https://ui.shadcn.com/docs/components/sidebar - shadcn sidebar docs
 */

import {
  LayoutDashboard,
  FileText,
  MapPin,
  Newspaper,
  Image,
  Users,
  Share2,
  Calendar,
  Settings,
  Zap,
  ShieldCheck,
  BarChart3,
  Camera,
  CheckSquare,
  MessageCircle,
  Building2,
  Database,
  Target,
  Star,
  type LucideIcon
} from 'lucide-react'

export interface NavItem {
  title: string
  href: string
  icon: LucideIcon
  description?: string
  badge?: string | number
  children?: NavItem[]
}

/**
 * Base navigation structure for the admin dashboard.
 * Badge counts are added dynamically via enhanceNavigationWithBadges().
 */
export const navigationConfig: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/',
    icon: LayoutDashboard,
    description: 'Overview and quick actions'
  },
  {
    title: 'Content Management',
    href: '/content',
    icon: FileText,
    description: 'Manage website content',
    children: [
      { title: 'Services', href: '/content/services', icon: Settings },
      { title: 'Locations', href: '/content/locations', icon: MapPin },
      { title: 'Blog Posts', href: '/content/blog', icon: Newspaper },
      { title: 'Static Pages', href: '/content/pages', icon: FileText },
      { title: 'Projects', href: '/content/projects', icon: Building2 },
      { title: 'Project Pages', href: '/content/project-pages', icon: FileText }
    ]
  },
  {
    title: 'Media & Gallery',
    href: '/media',
    icon: Image,
    description: 'Gallery and asset management',
    children: [
      { title: 'Gallery Management', href: '/media/galleries', icon: Camera },
      { title: 'Approval Queue', href: '/media/approval', icon: CheckSquare },
      { title: 'Asset Library', href: '/media/assets', icon: Database }
    ]
  },
  {
    title: 'Marketing & Social',
    href: '/marketing',
    icon: Share2,
    description: 'Social media and campaigns',
    children: [
      { title: 'Social Media', href: '/marketing/social', icon: Share2 },
      { title: 'Content Calendar', href: '/marketing/calendar', icon: Calendar }
    ]
  },
  {
    title: 'Customer Operations',
    href: '/customers',
    icon: Users,
    description: 'Leads and customer management',
    children: [
      { title: 'Leads Management', href: '/customers/leads', icon: MessageCircle },
      { title: 'Lead Attribution', href: '/customers/attribution', icon: Target },
      { title: 'Reviews', href: '/customers/reviews', icon: Star },
      { title: 'Customer Data', href: '/customers/data', icon: Users },
      { title: 'Jobber CRM', href: '/customers/jobber', icon: Building2 }
    ]
  },
  {
    title: 'Operations',
    href: '/operations',
    icon: Calendar,
    description: 'Scheduling and operations',
    children: [
      { title: 'Scheduler Config', href: '/scheduler', icon: Calendar }
    ]
  },
  {
    title: 'Integrations',
    href: '/integrations',
    icon: Zap,
    description: 'External service connections',
    children: [
      { title: 'Google My Business', href: '/integrations/gmb', icon: MapPin },
      { title: 'Analytics', href: '/integrations/analytics', icon: BarChart3 }
    ]
  },
  {
    title: 'Administration',
    href: '/admin',
    icon: ShieldCheck,
    description: 'System settings and security',
    children: [
      { title: 'User Management', href: '/admin/users', icon: Users },
      { title: 'Security & Audit', href: '/admin/security', icon: ShieldCheck },
      { title: 'Site Settings', href: '/admin/settings', icon: Settings }
    ]
  }
]

/**
 * Approval counts data structure from useApprovalCounts hook.
 */
export interface ApprovalCounts {
  totalPending?: number
  pendingReviews?: number
}

/**
 * Enhances navigation items with dynamic badge counts.
 *
 * @param items - Base navigation items
 * @param counts - Dynamic approval counts from useApprovalCounts hook
 * @returns Navigation items with badge counts applied
 */
export function enhanceNavigationWithBadges(
  items: NavItem[],
  counts?: ApprovalCounts
): NavItem[] {
  if (!counts) return items

  return items.map(item => {
    // Add badge to Approval Queue in Media & Gallery section
    if (item.title === 'Media & Gallery' && item.children) {
      return {
        ...item,
        children: item.children.map(child => {
          if (child.href === '/media/approval' && counts.totalPending) {
            return { ...child, badge: counts.totalPending }
          }
          return child
        })
      }
    }

    // Add badge to GMB in Integrations section
    if (item.title === 'Integrations' && item.children && counts.pendingReviews && counts.pendingReviews > 0) {
      return {
        ...item,
        children: item.children.map(child => {
          if (child.href === '/integrations/gmb') {
            return { ...child, badge: counts.pendingReviews }
          }
          return child
        })
      }
    }

    return item
  })
}
